/**
 * ProductionPreviewTile — the one preview tile every production station uses.
 *
 * An operator at Catching, Print, Pressing, Packing or Film Print is holding a
 * physical thing and asking "is this the right one?". These tiles are the
 * evidence they answer with. Every rule below exists because a tile that
 * asserts something it cannot prove turns a verification surface into a rubber
 * stamp (PRODUCTION_DESIGN §10): an empty slot sends the operator to go and
 * look, a wrong image makes them tick the box.
 *
 * THE GARMENT STATIONS SHOW THE PRODUCT, NEVER BARE ARTWORK. Print, Catching,
 * Pressing and Packing all render `buildFinishedGarmentPreviewEntries`, and it
 * emits pictures OF THE GARMENT only. What the operator needs to see is the
 * design ON the shirt; a bare print file on a checkerboard answers a question
 * nobody at those four stations is asking:
 *
 *   custom               ONE tile, no caption, WIDE frame. The customer's
 *                        combined preview already carries front and back in
 *                        one image, so it proves the product and neither
 *                        printed side, and a square frame would letterbox it
 *                        to half its height.
 *   standard, 2 mockups  TWO tiles the same size, captioned Front and Back.
 *   standard, 1 mockup   ONE tile, captioned with the side it actually is.
 *   standard, no mockup  ONE catalogue tile, no caption.
 *
 * Film Print is the ONE exception and does NOT use that builder: the operator
 * there is printing the FILM, so its artwork is the evidence. It supplies its
 * own single-source `artwork` + `productPreview` pair, which is why both of
 * those kinds and the `artwork` tier still exist here.
 *
 * THE FOUR RULES. None of them may soften.
 *
 *  1. THE TIER TRAVELS WITH THE URL, NEVER BY POSITION. De-duplication
 *     collapses a URL that is mapped twice, after which "index 1" is not
 *     reliably the mockup. Every source carries where it came from.
 *  2. THE LABEL NAMES WHAT RESOLVED, not what was hoped for. Only a tier that
 *     can prove a side keeps a side name; the catalogue photo says "Stock
 *     photo" and stops claiming a side at all. The two labels an operator must
 *     never confuse are the customer's rendered product ("Product preview")
 *     and the blank catalogue shot ("Stock photo"), so they do not share a
 *     first word. Same resolver feeds the lightbox caption, so the two can
 *     never disagree.
 *  3. A SIDE NEVER BORROWS THE OTHER SIDE'S URL, and the Shopify catalogue
 *     photo is FRONT-FACING, so it belongs to front chains only. A side with
 *     no image on file gets NO TILE at all, and a tile whose chain runs out at
 *     runtime stays EMPTY rather than falling sideways. This is the bug the
 *     whole feature exists to prevent: a front photo rendered under a label
 *     reading "Back".
 *  4. THE FOOTPRINT IS FIXED. Variants, not per-page CSS. Frame, caption slot
 *     and strip gap are constant for a variant whatever the tile is doing, so
 *     an image that arrives late or falls back cannot move the action controls
 *     under an operator's hand mid-scan. `frameShape` widens a frame without
 *     touching its height, so a wide tile and a square one still line up.
 *     A FILL variant keeps that promise by the same mechanism: its box is
 *     computed from the COLUMN it was given, never from the image in it, so
 *     loading, loaded, fallen back and exhausted are all the same box. What it
 *     gives up is only the pixel LITERAL. A page still supplies no sizing.
 *
 * Sizing (PRODUCTION_DESIGN §4.3, 48px minimum). Named by the JOB the tile is
 * doing, never by the station doing it. Six variants are a fixed number of
 * pixels; one FILLS the column it is handed:
 *   list       48px  a dense list row                — Picklist
 *   checklist  58px  a line checked off against the item in hand — Packing
 *   compact    64px  a dense card's identity pair    — Film Print
 *   reference  112px a supporting image beside a larger one
 *   card       160px the evidence on a station card
 *   hero       224px the one image a decision is made from
 *   comparison FILL  the garment in the operator's hand against the product it
 *                    is supposed to be — Print, Catching, Pressing. The strip
 *                    owns the card's whole preview column and divides it into
 *                    EQUAL tracks, so ONE tile is the column wide and TWO are
 *                    half of it each. Height comes from that width by a fixed
 *                    ratio — 2:1 for one tile, square for two — so it is still
 *                    the container and never the image that decides the box.
 *                    A fixed 224px would have rendered a custom at exactly the
 *                    size it was before the column was widened, because
 *                    `object-contain` sizes a 2:1 image off the HEIGHT (Brett,
 *                    2026-09-22, having been shown both: "Literally full
 *                    width, no cap").
 * The checklist variant's two-tile strip is ~196px wide. That is a LIST
 * footprint; it must not leak into the card-based stations.
 *
 * The page half of the flat lightbox list is built here too
 * (`buildProductionLightboxData`) and driven by `useProductionLightbox` next
 * door, so the rule that an empty slot is not openable is written once.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff, Maximize2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { thumbnailUrl, warmImage } from '@/lib/image-thumbnail';
import { cn } from '@/lib/utils';
import { PREVIEW_CHIP_CLASS, useHoverZoom } from './HoverZoom';

/* -------------------------------------------------------------------------
 * Vocabulary
 * ---------------------------------------------------------------------- */

/**
 * What a tile CLAIMS to show.
 *
 *  front / back    a printed side of the garment. The strongest claim there
 *                  is, and the only one that can be wrong in the dangerous
 *                  way, so only side-provable tiers keep the side name.
 *  artwork         the design, side unknown or combined.
 *  productPreview  the customer-facing preview of the finished product.
 *                  Proves the garment's appearance, never either print side.
 *  product         the catalogue photo of the blank/standard product.
 */
export type ProductionPreviewKind = 'front' | 'back' | 'artwork' | 'productPreview' | 'product';

/**
 * WHERE a url came from. Carried per url rather than inferred from position,
 * because the chain builder collapses a url that is mapped twice.
 *
 *  mockup   the design rendered ON the garment, for a known side.
 *  artwork  the bare print file. Side-correct, but not a picture of a shirt.
 *  preview  the combined customer product preview. NOT side-specific.
 *  catalog  the Shopify catalogue photo. Front-facing, and of the BLANK for a
 *           custom, so it proves the product and nothing about the print.
 */
export type ProductionPreviewTier = 'mockup' | 'artwork' | 'preview' | 'catalog';

/**
 * Per tier: the label a tile shows when THIS tier is what resolved, or `null`
 * when the tier can stand behind the slot's own claim and the slot keeps its
 * name.
 *
 * A Record and not a Set, deliberately. A fifth `ProductionPreviewTier` added
 * without answering this question is a COMPILE ERROR here. Behind a Set the new
 * tier would default to side-proving, which is the exact failure the whole
 * feature exists to prevent: a front-facing photo rendering under a "Back"
 * label. The cost of getting this wrong is an operator ticking off a print they
 * never actually looked at.
 */
const TIER_SELF_LABEL_KEY: Record<ProductionPreviewTier, string | null> = {
  // Proves the side it is filed under.
  mockup: null,
  artwork: null,
  // Side-blind: names itself instead, whatever slot it landed in.
  preview: 'preview.productPreview',
  catalog: 'preview.stockPhoto',
};

export interface ProductionPreviewSourceRef {
  url: string;
  tier: ProductionPreviewTier;
  /**
   * The full-size twin when `url` is a downscaled copy. The tile paints `url`;
   * the hover zoom and the lightbox show `fullUrl`. Rule 1 still holds: the
   * tier AND the twin travel with the url.
   */
  fullUrl?: string;
}

export interface ProductionPreviewEntry {
  kind: ProductionPreviewKind;
  /**
   * Ordered, de-duplicated source chain. The tile walks it on load error.
   * EMPTY is meaningful: "this print exists and we have no picture of it".
   */
  sources: ProductionPreviewSourceRef[];
}

/**
 * The preview facts a station response carries for one garment or line.
 *
 * Field names match the backend wire contract so a station page can hand the
 * item straight in. Everything is nullable because every one of these is an
 * org mapping that may simply not exist.
 */
export interface ProductionPreviewFacts {
  /** Shopify catalogue photo. FRONT-FACING. Front chains only, never back. */
  imageUrl: string | null;
  /** Signed, re-hosted customer product preview. Combined, never a side. */
  customPreviewUrl: string | null;
  /** The original customer product preview source. Same standing as above. */
  customPreviewSourceUrl: string | null;
  /**
   * Signed, re-hosted BACK preview mockup of a custom line, sent only when the
   * client's customiser supplies one (a per-client setting). Its presence
   * turns the one combined custom tile into a Front and a Back mockup tile.
   * Optional: only Packing sends it today.
   */
  customBackPreviewUrl?: string | null;
  /** The design shown ON the garment, front. Standards only. */
  frontMockupPreviewUrl: string | null;
  /** The design shown ON the garment, back. Never the front mockup. */
  backMockupPreviewUrl: string | null;
  /**
   * SUPPLIED BY THE BACKEND, DELIBERATELY UNREAD BY THE GARMENT POLICY.
   *
   * The bare print artwork per side: variant artwork for a standard, the
   * supplied side file for a custom. Every station response still carries
   * both, and they are the API-supplied SIDE data a two-sided custom would be
   * built from, so they are kept rather than deleted. No garment station
   * renders them: bare artwork was removed from Print, Catching, Pressing and
   * Packing on purpose, and `buildFinishedGarmentPreviewEntries` never reads
   * either field. They are not dead — do not delete them as such.
   */
  frontPreviewUrl: string | null;
  /** See `frontPreviewUrl`. Same standing, same reason for staying. */
  backPreviewUrl: string | null;
  /**
   * Whether this garment prints a front / a back.
   *
   * NOT read by the preview policy: which tiles exist is decided by which
   * IMAGES exist, so a side with nothing on file is simply absent rather than
   * drawn empty. These travel on the same object because the stations' own
   * controls are driven by them — the print station's AUTHORITATIVE side
   * detection (HEAD probed or read off the manifest) decides which Print
   * buttons appear, and no tile may change that.
   */
  hasFront: boolean;
  hasBack: boolean;
  /**
   * True when this garment carries the customer's own design rather than a
   * catalogue variant. REQUIRED: every station response carries it, and a
   * sixth station that forgets it must fail to compile rather than silently
   * inherit the wrong preview policy.
   */
  isCustomProduct: boolean;
}

/* -------------------------------------------------------------------------
 * Pure builders — shared by the tiles, the pages and the lightbox
 * ---------------------------------------------------------------------- */

/**
 * PURE. De-duplicating chain builder. The FIRST mention of a url keeps its
 * tier, so a mockup that also happens to be the catalogue photo stays a
 * mockup rather than being demoted by its second mention.
 */
export function buildPreviewSourceChain(
  candidates: ReadonlyArray<
    readonly [string | null | undefined, ProductionPreviewTier, (string | null | undefined)?]
  >,
): ProductionPreviewSourceRef[] {
  const seen = new Set<string>();
  const out: ProductionPreviewSourceRef[] = [];
  for (const [url, tier, fullUrl] of candidates) {
    if (typeof url !== 'string' || url.length === 0 || seen.has(url)) continue;
    seen.add(url);
    out.push(fullUrl && fullUrl !== url ? { url, tier, fullUrl } : { url, tier });
  }
  return out;
}

/**
 * PURE. Each source becomes its downscaled copy, then the SAME tier's
 * original, before the chain moves on to the next tier. A host that cannot
 * resize returns the url unchanged, so the pair collapses and the chain is
 * identical to the input: signed custom previews behave exactly as before.
 */
export function thumbnailFirstChain(
  sources: ReadonlyArray<ProductionPreviewSourceRef>,
  px: number,
): ProductionPreviewSourceRef[] {
  return buildPreviewSourceChain(
    sources.flatMap((s) => [
      [thumbnailUrl(s.url, px), s.tier, s.url] as const,
      [s.url, s.tier] as const,
    ]),
  );
}

/**
 * PURE. Translation key naming WHAT IS ON SCREEN, from the tile's kind and the
 * tier that actually resolved. The single source of truth for both the tile
 * caption and the lightbox caption.
 *
 * `tier` is undefined when the chain is empty or exhausted. The slot then
 * falls back to its own plain name, which is still true OF THE SLOT even
 * though there is no image in it.
 */
export function productionPreviewLabelKey(
  kind: ProductionPreviewKind,
  tier?: ProductionPreviewTier,
): string {
  // Rule 2 + rule 3, in one place: a tier that cannot prove the slot's claim
  // names ITSELF instead, whatever the slot wanted to be. Routed through
  // `tierProvesSide` so the side-proving rule has exactly one definition.
  if (tier !== undefined && !tierProvesSide(tier)) return TIER_SELF_LABEL_KEY[tier]!;
  switch (kind) {
    // A side tile is a side tile whatever proved it. The station screens show
    // product images only, so there is no longer a second, bare-artwork kind
    // of front to tell apart from this one.
    case 'front':
      return 'preview.front';
    case 'back':
      return 'preview.back';
    case 'artwork':
      return 'preview.artwork';
    case 'productPreview':
      return 'preview.productPreview';
    case 'product':
    default:
      return 'preview.stockPhoto';
  }
}

/**
 * True when this tier is allowed to keep the slot's own name, Front or Back
 * included. The single definition of the side-proving rule:
 * `productionPreviewLabelKey` above is its only production caller, and it is
 * exported so a station or a test can ask the question without re-deriving it.
 */
export function tierProvesSide(tier?: ProductionPreviewTier): boolean {
  return tier !== undefined && TIER_SELF_LABEL_KEY[tier] === null;
}

/** Stable key for a page's `(subject, tile) -> flat lightbox index` map. */
export function productionPreviewKey(subjectId: string, kind: ProductionPreviewKind): string {
  return `${subjectId}:${kind}`;
}

/**
 * PURE. The GARMENT-PREVIEW entry policy. Print, Catching, Pressing, Packing.
 *
 * All four stations answer the same question from the same evidence: is the
 * thing in my hand the thing this order asked for? So all four get pictures of
 * the GARMENT and nothing else. Bare print artwork is gone from every one of
 * them (Brett, 2026-09-21: "No artwork previews ... just show the image
 * preview on the garment, that is what we need to see").
 *
 *  CUSTOM WITH A BACK MOCKUP — TWO tiles, Front then Back.
 *      Only when the client's customiser sends a separate back mockup
 *      (`customBackPreviewUrl`): its main preview is then the FRONT mockup.
 *      Front: signed preview, its raw source, then the catalogue photo.
 *      Back:  the signed back mockup. NOTHING ELSE, by rule 3.
 *
 *  CUSTOM — ONE tile, always, whatever else is on file.
 *      The customer's combined preview carries front and back in a single
 *      image, so it needs no side label and it must never sit under one: it
 *      proves the garment, not either print. Signed preview, then its raw
 *      source, then the catalogue photo. The tile is emitted even when all
 *      three are missing, so the card keeps its shape and says out loud that
 *      there is no picture.
 *      Garment mockups are NEVER consulted: there is no catalogue variant
 *      behind a customer's own design.
 *
 *  STANDARD — one tile per side that HAS AN IMAGE, Front then Back.
 *      Front: its mockup, then the catalogue photo as a last resort.
 *      Back:  its mockup. NOTHING ELSE. The catalogue photo is a front-facing
 *             shot; under a "Back" label it is a picture of the wrong side,
 *             which is worse than no tile at all.
 *      A side with no mockup on file produces NO TILE ("If no back image, then
 *      just don't show the back image and just show one image"). That is not a
 *      repeal of rule 3, it is the same rule one step earlier: where the old
 *      code drew an empty Back box, this draws nothing.
 *
 *  STANDARD WITH NO SIDE MOCKUPS — ONE catalogue tile, uncaptioned.
 *      Byte for byte what an org with no mockup mapping saw before. Emitted
 *      even with no catalogue url, for the same fixed-footprint reason.
 *
 * `hasFront` / `hasBack` are deliberately NOT read HERE. Which tiles this
 * builder emits is decided by which images exist.
 *
 * A station MAY narrow that output to its own side state, and Print does: see
 * `buildPrintStationPreviewEntries` in `PrintStationPreviews.tsx`, which drops
 * a side tile the job does not print, because a card whose banner says "Back
 * only" must not show a tile captioned "Front". That is a one-way wire. Nothing
 * may run it the other way: no side state is ever derived from which pictures
 * happen to be on file. Do not delete Print's filter as a violation of this
 * paragraph; it is the exception this paragraph names.
 */
export function buildFinishedGarmentPreviewEntries(
  facts: ProductionPreviewFacts,
): ProductionPreviewEntry[] {
  if (facts.isCustomProduct && facts.customBackPreviewUrl) {
    return [
      {
        kind: 'front',
        sources: buildPreviewSourceChain([
          [facts.customPreviewUrl, 'mockup'],
          [facts.customPreviewSourceUrl, 'mockup'],
          [facts.imageUrl, 'catalog'],
        ]),
      },
      {
        kind: 'back',
        // No catalogue photo here, deliberately. See rule 3.
        sources: buildPreviewSourceChain([[facts.customBackPreviewUrl, 'mockup']]),
      },
    ];
  }

  if (facts.isCustomProduct) {
    return [
      {
        kind: 'productPreview',
        sources: buildPreviewSourceChain([
          [facts.customPreviewUrl, 'preview'],
          [facts.customPreviewSourceUrl, 'preview'],
          [facts.imageUrl, 'catalog'],
        ]),
      },
    ];
  }

  const entries: ProductionPreviewEntry[] = [];
  if (facts.frontMockupPreviewUrl) {
    entries.push({
      kind: 'front',
      sources: buildPreviewSourceChain([
        [facts.frontMockupPreviewUrl, 'mockup'],
        [facts.imageUrl, 'catalog'],
      ]),
    });
  }
  if (facts.backMockupPreviewUrl) {
    entries.push({
      kind: 'back',
      // No catalogue photo here, deliberately. See rule 3.
      sources: buildPreviewSourceChain([[facts.backMockupPreviewUrl, 'mockup']]),
    });
  }
  if (entries.length > 0) return entries;

  // Nothing side-specific on file: identify the product and claim no side.
  return [{ kind: 'product', sources: buildPreviewSourceChain([[facts.imageUrl, 'catalog']]) }];
}

/* -------------------------------------------------------------------------
 * The flat lightbox list
 * ---------------------------------------------------------------------- */

/**
 * A tile list sharing one subject and one key namespace. One garment at
 * Catching, Pressing and Print; one section per line at Packing, whose flat
 * list spans the whole order.
 */
export interface ProductionLightboxSection {
  /**
   * The namespace half of the key. MUST be the same value the tile's own open
   * handler passes to `productionPreviewKey`, or a click opens another image.
   */
  subjectId: string;
  /** What the images are OF. Copied onto every entry in this section. */
  subject: string;
  entries: ReadonlyArray<ProductionPreviewEntry & { thumbUrl?: string | null }>;
}

/** One openable image in the page's flat list. */
export interface ProductionLightboxEntry {
  /** The key this entry is registered under in `indexByKey`. */
  key: string;
  kind: ProductionPreviewKind;
  subject: string;
  sources: ProductionPreviewSourceRef[];
  /** Low-resolution placeholder for the full view. See ProductionImageLightbox. */
  thumbUrl?: string | null;
}

export interface ProductionLightboxData {
  entries: ProductionLightboxEntry[];
  indexByKey: Map<string, number>;
}

/**
 * PURE. Flattens the tiles on screen into the ONE list the arrows ride, in
 * section order, and maps each tile's key to its position in it.
 *
 * A TILE WITH NO SOURCE IS NOT IN THE LIST. There is nothing to open, the tile
 * is not clickable either, and an entry for it would make the arrows land on a
 * slot the operator cannot have been pointing at. That single rule was
 * hand-rolled on four pages before it lived here; two copies of it is two
 * chances for the list and the tiles to disagree about what exists.
 */
export function buildProductionLightboxData(
  sections: ReadonlyArray<ProductionLightboxSection>,
): ProductionLightboxData {
  const entries: ProductionLightboxEntry[] = [];
  const indexByKey = new Map<string, number>();
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.sources.length === 0) continue;
      const key = productionPreviewKey(section.subjectId, entry.kind);
      indexByKey.set(key, entries.length);
      entries.push({
        key,
        kind: entry.kind,
        subject: section.subject,
        sources: entry.sources,
        thumbUrl: entry.thumbUrl,
      });
    }
  }
  return { entries, indexByKey };
}

/** The empty list, as a stable identity — a page with nothing on screen. */
export const EMPTY_PRODUCTION_LIGHTBOX_DATA: ProductionLightboxData = {
  entries: [],
  indexByKey: new Map<string, number>(),
};

/* -------------------------------------------------------------------------
 * Rendering
 * ---------------------------------------------------------------------- */

/**
 * The variants whose footprint is a fixed number of pixels. A page may choose
 * the SHAPE of one of these (`frameShape`); it may never choose its size.
 */
export type ProductionPreviewFixedVariant =
  | 'list'
  | 'checklist'
  | 'compact'
  | 'reference'
  | 'card'
  | 'hero';

export type ProductionPreviewVariant = ProductionPreviewFixedVariant | 'comparison';

/**
 * The shape of the frame. Height is the VARIANT's job; this is only about the
 * aspect ratio of what is inside it.
 *   square  one view of a garment. Everything, by default.
 *   wide    a 2:1 frame for ONE image that already contains two views, which
 *           today means a custom's combined front-and-back preview. In a
 *           square frame `object-contain` letterboxes it to about half the
 *           height and the operator cannot read either side.
 *
 * On a FIXED variant a wide frame is double the width at the SAME height, and
 * the page picks it because only the page knows the image holds two views. On
 * a FILL variant the STRIP picks it from the tile count instead, because there
 * the shape is not a property of the image at all: it is how much of the
 * column this tile owns. See `ProductionPreviewStrip`.
 */
export type ProductionPreviewFrameShape = 'square' | 'wide';

/**
 * When the caption is shown.
 *   auto   a tile that CLAIMS A SIDE is always captioned, because Front and
 *          Back sitting beside each other have to be told apart without
 *          hovering (PRODUCTION_DESIGN §10). Everything else is captioned only
 *          once it has fallen past its first choice, which is the moment the
 *          caption starts carrying news: this is the stock photo, not the
 *          thing you asked for.
 *          A LONE GENERIC TILE THEREFORE HAS NO CAPTION. There is nothing to
 *          tell it apart from, and "Product preview" under the one product
 *          preview on the card is noise.
 *   none   no caption and NO RESERVED SLOT. Only for a strip of one-source
 *          tiles whose label can never change and is carried elsewhere.
 */
export type ProductionPreviewCaptionMode = 'auto' | 'none';

/**
 * The smallest a tile may ever compute to, in either dimension
 * (PRODUCTION_DESIGN §4.3). A fill variant states it twice on purpose: once as
 * the grid track's floor, once on the frame itself, because the two answer
 * different questions — how narrow a track may get, and how short the ratio
 * may make the box once it has. The Tailwind literals `min-h-[48px]` /
 * `min-w-[48px]` below are this number; keep them in step.
 */
const FILL_MIN_TARGET_PX = 48;

interface VariantStyle {
  /**
   * fixed  a footprint in pixels, the same at every screen width.
   * fill   a footprint taken from the container: the strip is a grid of equal
   *        tracks and the tile is its track wide. Still constant across every
   *        state of the tile at a given container width, which is the part of
   *        rule 4 that matters — nothing may move mid-scan.
   */
  layout: 'fixed' | 'fill';
  /** Column width. Bounds the caption; fixed so a caption cannot widen a row. */
  column: string;
  /** Column width for a `wide` frame. Always the wide frame's own width. */
  columnWide: string;
  /** The image frame. Also the hit target, so never below 48px. */
  frame: string;
  /**
   * The 2:1 frame. SAME HEIGHT, double the width, every time: a wide tile has
   * to sit on the same baseline as a square one and must never change how tall
   * the strip is. On a fill variant, where the width is the track and the
   * height follows from it, "wide" instead means the 2:1 ratio itself.
   */
  frameWide: string;
  icon: string;
  caption: string;
  /** Reserved caption height. Constant whether or not a caption is showing. */
  captionSlot: string;
  gap: string;
  /**
   * The persistent zoom affordance. `cursor-zoom-in` and the tooltip are both
   * hover-gated, and hover does not exist on a station screen
   * (PRODUCTION_DESIGN §10), so without this the full view is undiscoverable.
   *   none   no room for it; the tile is small enough to read as one target
   *   icon   glyph only
   *   label  glyph plus the word
   */
  zoomChip: 'none' | 'icon' | 'label';
  /**
   * Warm each source's full-size twin (`fullUrl`) in the background once its
   * small copy has loaded. Only for a short checklist of a few images: on a
   * long list, warming every row's original would make the screen slower.
   */
  warmFullSize: boolean;
  /**
   * Whether an exhausted tile says "Preview unavailable" INSIDE the frame.
   * Over an empty box the caption alone ("Back") reads as "the back print is
   * missing", which is alarming and wrong: it means there is no renderable
   * preview of it. Small variants keep it in the accessible name
   * only, because two lines of text do not fit a 48px tile.
   */
  emptyWords: boolean;
}

const VARIANT_STYLES: Record<ProductionPreviewVariant, VariantStyle> = {
  list: {
    layout: 'fixed',
    column: 'w-24',
    columnWide: 'w-24',
    frame: 'h-12 w-12',
    frameWide: 'h-12 w-24',
    icon: 'h-5 w-5',
    caption: 'text-sm',
    captionSlot: 'h-4',
    gap: 'gap-1',
    zoomChip: 'none',
    warmFullSize: false,
    emptyWords: false,
  },
  checklist: {
    layout: 'fixed',
    // The caption is OUT OF FLOW, so the column is exactly frame-tall and a
    // row's items-center centres the FRAME. It hangs 18px below the frame
    // (mt-0.5 + h-4), so the container must supply at least 20px of padding.
    column: 'relative w-24',
    columnWide: 'relative w-[116px]',
    frame: 'h-[58px] w-[58px]',
    frameWide: 'h-[58px] w-[116px]',
    icon: 'h-6 w-6',
    caption: 'text-sm',
    captionSlot: 'absolute inset-x-0 top-full mt-0.5 h-4',
    gap: 'gap-1',
    zoomChip: 'none',
    warmFullSize: true,
    emptyWords: false,
  },
  compact: {
    layout: 'fixed',
    column: 'w-16',
    columnWide: 'w-32',
    frame: 'h-16 w-16',
    frameWide: 'h-16 w-32',
    icon: 'h-6 w-6',
    caption: 'text-sm',
    captionSlot: 'h-4',
    gap: 'gap-1.5',
    zoomChip: 'none',
    warmFullSize: false,
    emptyWords: false,
  },
  reference: {
    layout: 'fixed',
    column: 'w-28',
    columnWide: 'w-56',
    frame: 'h-28 w-28',
    frameWide: 'h-28 w-56',
    icon: 'h-7 w-7',
    caption: 'text-sm',
    captionSlot: 'h-5',
    gap: 'gap-2',
    zoomChip: 'icon',
    warmFullSize: false,
    emptyWords: true,
  },
  card: {
    layout: 'fixed',
    column: 'w-40',
    columnWide: 'w-80',
    frame: 'h-40 w-40',
    frameWide: 'h-40 w-80',
    icon: 'h-8 w-8',
    caption: 'text-base',
    captionSlot: 'h-5',
    gap: 'gap-2',
    zoomChip: 'label',
    warmFullSize: false,
    emptyWords: true,
  },
  hero: {
    layout: 'fixed',
    column: 'w-56',
    columnWide: 'w-[28rem]',
    frame: 'h-56 w-56',
    frameWide: 'h-56 w-[28rem]',
    icon: 'h-10 w-10',
    caption: 'text-base',
    captionSlot: 'h-5',
    gap: 'gap-3',
    zoomChip: 'label',
    warmFullSize: false,
    emptyWords: true,
  },
  comparison: {
    layout: 'fill',
    // The TRACK is the width. `min-w-0` so a long caption cannot push a grid
    // item past its track and make two tiles unequal — the one thing this
    // variant exists to guarantee.
    column: 'w-full min-w-0',
    columnWide: 'w-full min-w-0',
    // TWO tiles: the column is shared, so each track is already about half as
    // wide as the card and a square reads the mockup at close to full height.
    // The 48px floor is stated on the box as well as on the track, because the
    // ratio is what could breach it: at a track's 48px minimum a 2:1 frame
    // computes 24px tall, and the floor wins over the ratio.
    frame: 'aspect-square w-full min-h-[48px] min-w-[48px]',
    // ONE tile: it owns the whole column. 2:1 rather than square because the
    // image that gets this case is a custom's combined front-and-back preview,
    // itself about 2:1 — in a full-width SQUARE frame `object-contain` would
    // render exactly the same picture with a third of the card empty above and
    // below it. A one-sided standard and a catalogue fallback get this frame
    // too, so a custom and a one-sided standard have the same footprint and
    // the square source sits centred in it (Brett, 2026-09-22: "it can keep
    // the full screen like custom has and just center the image").
    frameWide: 'aspect-[2/1] w-full min-h-[48px] min-w-[48px]',
    icon: 'h-10 w-10',
    caption: 'text-base',
    captionSlot: 'h-5',
    gap: 'gap-3',
    zoomChip: 'label',
    warmFullSize: false,
    emptyWords: true,
  },
};

export interface ProductionPreviewTileProps {
  entry: ProductionPreviewEntry;
  /**
   * What the image is OF: the product name, or an order number and SKU. Names
   * the alt text and the open-image control. Must not be blank.
   */
  subject: string;
  variant?: ProductionPreviewVariant;
  /**
   * Square unless the ONE image in this tile already holds two views. On a
   * fill variant this is the strip's call rather than a page's, and it means
   * "this tile owns the whole column" — see `ProductionPreviewStrip`.
   */
  frameShape?: ProductionPreviewFrameShape;
  captionMode?: ProductionPreviewCaptionMode;
  /**
   * Opens the page's lightbox at this tile's image. `sourceIndex` is the
   * position in the chain that ACTUALLY rendered, so the full view opens on
   * the image the operator is pointing at rather than on a first choice this
   * tile has already watched fail.
   */
  onOpen?: (sourceIndex: number) => void;
}

/**
 * One tile plus its caption. It owns the walk through its own source chain,
 * which makes it the only thing that knows which tier is on screen. That is
 * why the caption lives here and not in the parent.
 */
export function ProductionPreviewTile({
  entry,
  subject,
  variant = 'list',
  frameShape = 'square',
  captionMode = 'auto',
  onOpen,
}: ProductionPreviewTileProps) {
  const { t } = useTranslation('production');
  const style = VARIANT_STYLES[variant];
  const frameSize = frameShape === 'wide' ? style.frameWide : style.frame;
  const columnSize = frameShape === 'wide' ? style.columnWide : style.column;
  const sourceKey = entry.sources.map((source) => source.url).join('|');
  const [sourceIndex, setSourceIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // A re-signed url must be retried, not written off.
  useEffect(() => {
    setSourceIndex(0);
  }, [sourceKey]);

  const active = entry.sources[sourceIndex];
  const src = active?.url;

  // Reset the skeleton on every source change, and clear it immediately for an
  // image the browser already has cached (whose `load` fires before React can
  // attach the handler) so a warm tile never sits under a pulsing placeholder.
  useLayoutEffect(() => {
    const el = imgRef.current;
    setLoaded(!!el && el.complete && el.naturalWidth > 0);
  }, [src]);

  const label = t(productionPreviewLabelKey(entry.kind, active?.tier));
  // A tile earns a caption by CLAIMING A SIDE, or by no longer showing its
  // first choice. A lone product preview claims nothing and says nothing.
  const isSideSlot = entry.kind === 'front' || entry.kind === 'back';
  // A thumb falling back to its own original is still the first choice.
  const fellBack = sourceIndex > 0 && src !== entry.sources[0]?.fullUrl;
  const captionVisible = captionMode === 'auto' && (isSideSlot || fellBack);
  const neverHadSource = entry.sources.length === 0;

  // The slot is rendered whether or not there is anything in it. A tile that
  // gains a caption on fallback must not grow the row it is sitting in.
  const caption =
    captionMode === 'none' ? null : (
      <span className={cn('flex w-full items-center justify-center', style.captionSlot)}>
        {captionVisible ? (
          <span
            className={cn(
              'max-w-full truncate font-medium leading-none text-muted-foreground',
              style.caption,
            )}
            title={label}
          >
            {label}
          </span>
        ) : null}
      </span>
    );

  const frame = cn(
    'relative flex items-center justify-center overflow-hidden rounded',
    frameSize,
  );
  const column = cn('flex flex-col items-center gap-1', columnSize);

  // A short checklist (e.g. the Packer) warms the full-size once the small
  // copy is on screen, so the zoom and the lightbox open on a cached image.
  const preloadUrl = style.warmFullSize && loaded ? active?.fullUrl : undefined;
  useEffect(() => {
    if (!preloadUrl) return;
    const warm = () => warmImage(preloadUrl, { low: true });
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(warm);
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(warm, 0);
    return () => window.clearTimeout(id);
  }, [preloadUrl]);

  // The zoom reads the tile's OWN active source and label, so it can never
  // show a different tier than the tile. Called before the early return
  // (rules of hooks); an empty or exhausted tile has no image span to hover.
  const zoom = useHoverZoom(active?.fullUrl ?? src, {
    label,
    placeholderSrc: active?.fullUrl ? src : undefined,
  });

  if (!src) {
    return (
      <div className={column}>
        <div
          className={cn(
            frame,
            'flex-col gap-1.5 border border-dashed border-muted-foreground/40 bg-muted/30',
          )}
          role="img"
          aria-label={
            neverHadSource
              ? `${label}: ${t('preview.unavailable')}`
              : t('preview.unavailable')
          }
          title={t('preview.unavailable')}
        >
          <ImageOff className={cn(style.icon, 'text-muted-foreground')} aria-hidden="true" />
          {/* Say it in words wherever there is room. The frame is fixed either
              way, so nothing moves. See VariantStyle.emptyWords. */}
          {style.emptyWords && (
            <span
              className={cn(
                'max-w-full px-2 text-center leading-tight text-muted-foreground',
                style.caption,
              )}
              aria-hidden="true"
            >
              {t('preview.unavailable')}
            </span>
          )}
        </div>
        {caption}
      </div>
    );
  }

  const image = (
    <span className={cn(frame, 'artwork-checker border')} {...zoom.hoverProps}>
      {!loaded && <Skeleton className="absolute inset-0 rounded-none motion-reduce:animate-none" />}
      <img
        ref={imgRef}
        src={src}
        alt={`${subject} ${label}`}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setSourceIndex((i) => i + 1)}
        className={cn(
          // object-contain, not object-cover: cover crops a square out of the
          // middle, and on a garment that is the neckline and the top of the
          // print, which is exactly what the operator is checking. The checker
          // backdrop is for the bare print file, where a transparent PNG of a
          // white-ink design reads as an empty square on a pale tile.
          //
          // A FILL frame takes its height from its own aspect-ratio, so the
          // image is pinned to the frame's box instead of asking for 100% of a
          // height that is itself being derived. Same painted result, same
          // stacking over the skeleton, no percentage-resolution question.
          style.layout === 'fill' ? 'absolute inset-0' : 'relative',
          'h-full w-full object-contain',
          'transition-opacity duration-150 motion-reduce:transition-none',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </span>
  );

  return (
    <div className={column}>
      {onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(sourceIndex)}
          aria-label={t('preview.openImage', { label, subject })}
          // Supplementary only: the accessible name above already carries the
          // label, and a station operator never needs to hover
          // (PRODUCTION_DESIGN §10). The mouse-only zoom lives in HoverZoom.
          title={label}
          className={cn(
            'relative flex items-center justify-center overflow-hidden rounded',
            frameSize,
            'cursor-zoom-in ring-offset-background transition-opacity duration-150',
            // Hover is guarded: on a station touchscreen an unguarded hover
            // state sticks after the tap (PRODUCTION_DESIGN §10).
            '[@media(hover:hover)]:hover:opacity-80',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'motion-reduce:transition-none',
          )}
        >
          {image}
          {/* A PERSISTENT affordance, not a hover one. See
              VariantStyle.zoomChip. Not announced: the button's accessible
              name already says "Open the ... image". */}
          {style.zoomChip !== 'none' && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute bottom-1 right-1 flex items-center gap-1',
                PREVIEW_CHIP_CLASS,
              )}
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
              {style.zoomChip === 'label' && t('preview.zoom')}
            </span>
          )}
        </button>
      ) : (
        image
      )}
      {caption}
      {zoom.node}
    </div>
  );
}

interface ProductionPreviewStripBaseProps {
  entries: ProductionPreviewEntry[];
  /** What the images are OF. Passed to every tile. Must not be blank. */
  subject: string;
  captionMode?: ProductionPreviewCaptionMode;
  /** `sourceIndex` is the chain position that actually rendered. */
  onOpen?: (kind: ProductionPreviewKind, sourceIndex: number) => void;
}

export type ProductionPreviewStripProps =
  | (ProductionPreviewStripBaseProps & {
      variant?: ProductionPreviewFixedVariant;
      /**
       * Applied to EVERY tile in the strip, so a row is never half wide and
       * half square. A garment is one kind of thing or the other: a custom's
       * single combined preview is wide, a standard's side tiles are square
       * and equal.
       */
      frameShape?: ProductionPreviewFrameShape;
    })
  | (ProductionPreviewStripBaseProps & {
      variant: 'comparison';
      /**
       * A COMPILE ERROR, deliberately. On a fill variant the shape is not the
       * page's to choose: it is how much of the column a tile owns, which is
       * the tile COUNT, which only the strip knows. A page that could pass
       * `wide` here could give a standard's Front and Back different frames
       * from a custom's, which is the asymmetry this variant removes.
       */
      frameShape?: never;
    });

/**
 * A row of tiles. One row, whatever the variant: two side tiles sit beside
 * each other rather than one becoming a hero.
 *
 * Renders nothing for an empty list. A layout that needs a stable height
 * should always be handed at least one entry, which is exactly why the
 * builders emit honest empty entries instead of dropping them.
 */
export function ProductionPreviewStrip(props: ProductionPreviewStripProps) {
  const { entries, subject, variant = 'list', captionMode = 'auto', onOpen } = props;
  const style = VARIANT_STYLES[variant];
  if (entries.length === 0) return null;

  const tile = (entry: ProductionPreviewEntry, frameShape: ProductionPreviewFrameShape) => (
    <ProductionPreviewTile
      key={entry.kind}
      entry={entry}
      subject={subject}
      variant={variant}
      frameShape={frameShape}
      captionMode={captionMode}
      onOpen={onOpen ? (sourceIndex: number) => onOpen(entry.kind, sourceIndex) : undefined}
    />
  );

  if (style.layout === 'fill') {
    return (
      // A GRID, not a flex row, because the one thing that has to be exactly
      // true is that two tiles are exactly equal. Grid subtracts the gap first
      // and divides what is left, so equal `1fr` tracks are equal to the pixel;
      // two 50%-wide flex children plus a gap overflow the row, and `flex-1`
      // children can still be pulled apart by unequal min-content pressure.
      //
      // The track list is an INLINE STYLE on purpose: it is computed from the
      // tile count, and a class name built from a variable is a class Tailwind
      // never sees and so never generates.
      //
      // `minmax(48px, 1fr)` is the hit-target floor (PRODUCTION_DESIGN §4.3)
      // stated as the track's own minimum: below the width where two tracks
      // and a gap fit, the strip overflows rather than shrinking a target the
      // operator has to hit in gloves.
      <div
        className={cn('grid w-full items-start', style.gap)}
        style={{
          gridTemplateColumns: `repeat(${entries.length}, minmax(${FILL_MIN_TARGET_PX}px, 1fr))`,
        }}
      >
        {/* ONE tile owns the whole column, so it gets the 2:1 frame; when the
            column is shared the tiles are square. That is the whole shape
            rule, in one place, for all three stations — and it is why a custom
            and a one-sided standard come out identical without either page
            knowing which it has. */}
        {entries.map((entry) => tile(entry, entries.length === 1 ? 'wide' : 'square'))}
      </div>
    );
  }

  const frameShape = props.frameShape ?? 'square';
  return (
    <div className={cn('flex flex-shrink-0 items-start', style.gap)}>
      {entries.map((entry) => tile(entry, frameShape))}
    </div>
  );
}
